import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, CalendarCheck, Check, Video, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import {
  bookingContextFromPath,
  CONSULTATION_TYPE,
  DURATION_MINUTES,
} from "../lib/bookingConfigs";
import { toast } from "sonner";

const DIAG_EVENT = "ac-open-diagnostica";

export function openDiagnostica() {
  window.dispatchEvent(new CustomEvent(DIAG_EVENT));
}

const GOALS = [
  { v: "ac-start", l: "Analizzare/verificare la conformità di un immobile", t: "AC START" },
  { v: "ac-hunt", l: "Cercare e acquistare un immobile", t: "AC HUNT" },
  { v: "ac-select", l: "Vendere un immobile di proprietà", t: "AC SELECT" },
  { v: "ac-invest", l: "Operazione di investimento immobiliare", t: "AC INVEST" },
  { v: "ac-management", l: "Ristrutturazione / gestione cantiere", t: "AC MANAGEMENT" },
];

const SLOTS = [
  ...["09", "10", "11", "12"].flatMap((h) => ["00", "20", "40"].map((m) => `${h}:${m}`)),
  ...["15", "16", "17", "18"].flatMap((h) => ["00", "20", "40"].map((m) => `${h}:${m}`)),
];

const GOLD = "#d4af37";

function nextBusinessDays(count = 14) {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < count) {
    if (d.getDay() !== 0 && d.getDay() !== 6) {
      days.push({
        iso: d.toISOString().slice(0, 10),
        dow: ["DOM", "LUN", "MAR", "MER", "GIO", "VEN", "SAB"][d.getDay()],
        num: String(d.getDate()).padStart(2, "0"),
        mon: ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"][d.getMonth()],
      });
    }
    d.setDate(d.getDate() + 1);
  }
  return days;
}

export function DiagnosticaProvider({ children }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(DIAG_EVENT, onOpen);
    return () => window.removeEventListener(DIAG_EVENT, onOpen);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  return (
    <>
      {children}
      <AnimatePresence>
        {open && <DiagnosticaModal close={close} />}
      </AnimatePresence>
    </>
  );
}

function DiagnosticaModal({ close }) {
  const ctx = useMemo(() => bookingContextFromPath(), []);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => ({
    name: "",
    email: "",
    phone: "",
    goal: ctx.bookingSource === "homepage" ? "" : ctx.configKey,
    selected_goal: "",
    selected_type: "",
    situation: "",
  }));
  const [slot, setSlot] = useState(null);
  const [sending, setSending] = useState(false);

  const [meetLink, setMeetLink] = useState("");

  const confirm = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        date: slot.date,
        time: slot.time,
        source: ctx.bookingSource,
        consultation_type: CONSULTATION_TYPE,
        duration_minutes: DURATION_MINUTES,
      });
      setMeetLink(data.meet_link || "");
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="diagnostica-overlay"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={close} />
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.98 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 max-h-[94vh] w-full max-w-[620px] overflow-y-auto border bg-[#0d0d0d] p-6 shadow-[0_40px_90px_rgba(0,0,0,0.75)] sm:p-9"
        style={{ borderColor: `${GOLD}66` }}
        role="dialog"
        aria-modal="true"
        data-testid="diagnostica-modal"
      >
        <button onClick={close} aria-label="Chiudi" data-testid="diagnostica-modal-close"
          className="absolute right-4 top-4 p-2 text-[#929292] transition-colors hover:text-gold-light">
          <X size={18} />
        </button>
        <p className="eyebrow" data-testid="diagnostica-badge">Videoconsulenza gratuita • 20 minuti</p>
        <h3 className="display mt-4 text-2xl sm:text-3xl" data-testid="diagnostica-title">
          {ctx.config.title}
        </h3>
        <p className="mt-3 max-w-[520px] text-[14px] leading-relaxed text-[#c9c6bf]" data-testid="diagnostica-description">
          {ctx.config.description}
        </p>
        {step === 1 && <StepQualifica ctx={ctx} form={form} setForm={setForm} onNext={() => setStep(2)} />}
        {step === 2 && <StepCalendar slot={slot} setSlot={setSlot} onBack={() => setStep(1)} onConfirm={confirm} sending={sending} />}
        {step === 3 && <StepConferma ctx={ctx} form={form} slot={slot} meetLink={meetLink} close={close} />}
      </motion.div>
    </motion.div>
  );
}

function Field({ label, children, dataTestId }) {
  return (
    <label className="block">
      <span className="text-[9px] font-bold tracking-[0.2em] text-[#929292]">{label}</span>
      <div className="mt-2">{children}</div>
      {dataTestId && <span data-testid={dataTestId} className="hidden" />}
    </label>
  );
}

const inputCls =
  "w-full border border-[#c9a45c]/30 bg-[#111111] px-4 py-3 text-[14px] text-cream outline-none transition-colors duration-200 placeholder:text-[#5c5c5c] focus:border-[#d4af37]";

function StepQualifica({ ctx, form, setForm, onNext }) {
  const [error, setError] = useState("");
  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setError("");
  };
  const config = ctx.config;

  const submit = (e) => {
    e.preventDefault();
    const digits = form.phone.replace(/\D/g, "");
    if (form.name.trim().length < 2) return setError("Inserisci nome e cognome");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError("Inserisci una email valida");
    if (form.phone.trim() && digits.length < 8) return setError("Inserisci un recapito telefonico valido");
    if (config.showProtocolSelection && !form.goal) return setError("Seleziona il tuo obiettivo immobiliare");
    for (const q of config.questions || []) {
      if (!form[q.key]) return setError(`${q.label}: seleziona una risposta`);
    }
    setError("");
    onNext();
  };

  const optionCls = (selected) =>
    `border px-4 py-3 text-left text-[13px] transition-colors duration-200 ${
      selected
        ? "border-[#d4af37] bg-[#d4af37]/10 text-cream"
        : "border-[#c9a45c]/25 text-[#a8a5a0] hover:border-[#d4af37]/50 hover:text-cream"
    }`;

  return (
    <form onSubmit={submit} className="mt-6" data-testid="diagnostica-step-1">
      {config.showProtocolSelection && (
        <>
          <p className="mt-2 text-[9px] font-bold tracking-[0.2em] text-[#a8a5a0]">
            QUAL È IL TUO OBIETTIVO IMMOBILIARE PRIORITARIO? *
          </p>
          <div className="mt-3 grid gap-2">
            {GOALS.map((g) => (
              <button
                key={g.v}
                type="button"
                data-testid={`diagnostica-goal-${g.v}`}
                onClick={() => setForm((f) => ({ ...f, goal: g.v }))}
                className={`flex items-center justify-between ${optionCls(form.goal === g.v)}`}
              >
                <span>{g.l}</span>
                <span className="ml-3 shrink-0 text-[9px] font-bold tracking-[0.16em] text-gold">{g.t}</span>
              </button>
            ))}
            <button
              type="button"
              data-testid="diagnostica-goal-general"
              onClick={() => setForm((f) => ({ ...f, goal: "general" }))}
              className={`flex items-center justify-between ${optionCls(form.goal === "general")}`}
            >
              <span>Non so ancora quale percorso scegliere</span>
              <span className="ml-3 shrink-0 text-[9px] font-bold tracking-[0.16em] text-gold">A.C. HUB</span>
            </button>
          </div>
        </>
      )}

      {(config.questions || []).map((q, qi) => (
        <div key={q.key} className={qi === 0 && !config.showProtocolSelection ? "mt-2" : "mt-6"}>
          <p className="text-[9px] font-bold tracking-[0.2em] text-[#a8a5a0]">{q.label.toUpperCase()} *</p>
          <div className="mt-3 grid gap-2">
            {q.options.map((opt, oi) => (
              <button
                key={opt}
                type="button"
                data-testid={`diagnostica-${q.key}-${oi + 1}`}
                onClick={() => setForm((f) => ({ ...f, [q.key]: opt }))}
                className={optionCls(form[q.key] === opt)}
              >
                <span>{opt}</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field label="NOME E COGNOME *">
          <input data-testid="diagnostica-name" className={inputCls} placeholder="Mario Rossi" value={form.name} onChange={set("name")} />
        </Field>
        <Field label="EMAIL *">
          <input data-testid="diagnostica-email" type="email" className={inputCls} placeholder="mario@email.it" value={form.email} onChange={set("email")} />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="RECAPITO TELEFONICO (FACOLTATIVO)">
          <input data-testid="diagnostica-phone" type="tel" className={inputCls} placeholder="+39 333 0000000" value={form.phone} onChange={set("phone")} />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="DESCRIVI BREVEMENTE LA TUA ESIGENZA (FACOLTATIVO)">
          <textarea data-testid="diagnostica-situation" rows={3} className={`${inputCls} resize-none`} placeholder="Es. possiedo un immobile a Milano e voglio capirne il valore…" value={form.situation} onChange={set("situation")} />
        </Field>
      </div>
      {error && <p className="mt-4 text-[12px] text-[#e07a5f]" data-testid="diagnostica-error">{error}</p>}
      <button type="submit" data-testid="diagnostica-step1-next" className="btn btn-gold mt-6 w-full !min-h-[48px]">
        Scegli data e ora <CalendarCheck size={15} />
      </button>
    </form>
  );
}

function StepCalendar({ slot, setSlot, onBack, onConfirm, sending }) {
  const days = useMemo(() => nextBusinessDays(14), []);
  const [taken, setTaken] = useState([]);

  useEffect(() => {
    if (!slot?.date) return;
    let alive = true;
    api
      .get(`/bookings/availability?date=${slot.date}`)
      .then(({ data }) => alive && setTaken(data.taken || []))
      .catch(() => alive && setTaken([]));
    return () => {
      alive = false;
    };
  }, [slot?.date]);
  return (
    <div className="mt-6" data-testid="diagnostica-step-2">
      <p className="text-[9px] font-bold tracking-[0.2em] text-[#929292]">1. SCEGLI IL GIORNO (LUN-VEN)</p>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-2" data-testid="diagnostica-days">
        {days.map((d) => (
          <button
            key={d.iso}
            type="button"
            data-testid={`diagnostica-day-${d.iso}`}
            onClick={() => setSlot({ ...slot, date: d.iso, dow: d.dow, num: d.num, mon: d.mon, time: null })}
            className={`flex w-[64px] shrink-0 flex-col items-center border px-2 py-3 transition-colors duration-200 ${
              slot?.date === d.iso ? "border-[#d4af37] bg-[#d4af37]/10 text-cream" : "border-[#c9a45c]/25 text-[#929292] hover:border-[#d4af37]/50 hover:text-cream"
            }`}
          >
            <span className="text-[9px] tracking-[0.16em]">{d.dow}</span>
            <span className="mt-1 font-serif text-xl text-gold-light">{d.num}</span>
            <span className="text-[9px]">{d.mon}</span>
          </button>
        ))}
      </div>
      {slot?.date && (
        <>
          <p className="mt-5 text-[9px] font-bold tracking-[0.2em] text-[#929292]">2. SCEGLI L'ORARIO (SLOT DA 20 MINUTI)</p>
          <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6" data-testid="diagnostica-slots">
            {SLOTS.map((t) => {
              const occupato = taken.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  disabled={occupato}
                  title={occupato ? "Orario non disponibile" : "Disponibile"}
                  data-testid={`diagnostica-slot-${t.replace(":", "")}`}
                  data-occupato={occupato || undefined}
                  onClick={() => setSlot({ ...slot, time: t })}
                  className={`border px-2 py-2.5 text-[12px] font-semibold transition-colors duration-200 ${
                    slot?.time === t
                      ? "border-[#d4af37] bg-[#d4af37] text-[#0d0d0d]"
                      : occupato
                        ? "cursor-not-allowed border-[#c9a45c]/12 text-[#4a4a4a] line-through"
                        : "border-[#c9a45c]/25 text-[#929292] hover:border-[#d4af37]/50 hover:text-cream"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </>
      )}
      {!slot?.date && (
        <p className="mt-5 text-[13px] text-[#929292]" data-testid="diagnostica-days-hint">
          Seleziona un giorno per vedere gli orari disponibili: slot da 20 minuti, 9:00–12:40 e 15:00–18:40.
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button type="button" onClick={onBack} data-testid="diagnostica-step2-back" className="btn btn-outline !min-h-[46px] !px-5">
          <ArrowLeft size={13} /> Indietro
        </button>
        <button
          type="button"
          disabled={!slot?.time || sending}
          onClick={onConfirm}
          data-testid="diagnostica-confirm-button"
          className="btn btn-gold flex-1 !min-h-[46px] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {sending ? "Prenoto…" : slot?.time ? `Conferma: ${slot.dow} ${slot.num} ${slot.mon} · ${slot.time}` : "Conferma prenotazione"}
        </button>
      </div>
    </div>
  );
}

function StepConferma({ ctx, form, slot, meetLink, close }) {
  return (
    <div className="mt-6" data-testid="diagnostica-step-3">
      <div className="flex h-14 w-14 items-center justify-center border border-[#d4af37] text-[#d4af37]">
        <Check size={26} />
      </div>
      <p className="mt-5 text-[15px] leading-relaxed text-cream" data-testid="diagnostica-conferma-messaggio">
        La tua videoconsulenza {ctx.config.protocolLabel || "A.C. HUB"} di 20 minuti è confermata. Riceverai una
        email di conferma con tutti i dettagli. Analizzeremo le tue risposte prima della chiamata per offrirti
        massima chiarezza e valore.
      </p>
      {meetLink && (
        <a
          href={meetLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="diagnostica-meet-link"
          className="mt-5 flex items-center justify-between border border-[#d4af37]/60 bg-[#111111] px-5 py-4 text-[13px] font-semibold text-gold-light transition-colors duration-200 hover:bg-[#d4af37]/10"
        >
          <span>Il tuo link della video-call</span>
          <Video size={16} className="text-gold" />
        </a>
      )}
      <div className="mt-5 border border-[#c9a45c]/25 bg-[#111111] p-5 text-[13px] leading-relaxed text-[#929292]" data-testid="diagnostica-riepilogo">
        <span className="font-semibold text-gold-light">{slot?.dow} {slot?.num} {slot?.mon} · ore {slot?.time}</span>
        <br />
        {form.name} · {form.email} · {form.phone}
      </div>
      <button type="button" onClick={close} data-testid="diagnostica-done-button" className="btn btn-gold mt-6 w-full !min-h-[48px]">
        Perfetto, ho capito
      </button>
    </div>
  );
}
